import english from './en.json';

export type Locale = 'vi' | 'en';
export const parseLocale = (value: unknown): Locale => value === 'en' ? 'en' : 'vi';
// A suggestion only: explicit page URLs always determine the public language.
export const preferredLocale = (languages: readonly string[]): Locale => languages[0]?.toLowerCase().split('-')[0] === 'vi' ? 'vi' : 'en';
const messages: Record<string, string> = english;
const normalize = (text: string) => text.replace(/\s+/g, ' ').trim();
const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const patterns = Object.entries(messages).filter(([key]) => /\{\d+\}/.test(key)).sort(([a], [b]) => b.replace(/\{\d+\}/g, '').length - a.replace(/\{\d+\}/g, '').length).map(([key, translated]) => {
  const indexes: string[] = [];
  const parts = key.split(/(\{\d+\})/).map(part => {
    if (/^\{\d+\}$/.test(part)) { indexes.push(part.slice(1, -1)); return '(.*?)'; }
    return escapeRegex(part);
  });
  return { pattern: new RegExp(`^${parts.join('')}$`), translated, indexes };
});

/** Translates source-owned interface messages. Never pass editable user content here. */
export function translate(locale: Locale, message: string): string;
export function translate(locale: Locale, message: string | undefined): string | undefined;
export function translate(locale: Locale, message: string | undefined): string | undefined {
  if (locale === 'vi' || !message) return message;
  const key = normalize(message);
  let result = messages[key];
  if (result === undefined) {
    for (const entry of patterns) {
      const match = entry.pattern.exec(key);
      if (!match) continue;
      const values = Object.fromEntries(entry.indexes.map((index, i) => [index, match[i + 1]]));
      result = entry.translated.replace(/\{(\d+)\}/g, (_, index: string) => values[index] ?? '');
      break;
    }
  }
  return result === undefined ? message : `${message.match(/^\s*/)?.[0] ?? ''}${result}${message.match(/\s*$/)?.[0] ?? ''}`;
}

export function searchableText(locale: Locale, values: (string | undefined)[]) {
  return values.flatMap(value => value ? [value, translate(locale, value)] : []).join(' ').toLocaleLowerCase(locale);
}
